import { createRng, hashSeed } from '../rng.js';

/**
 * Trash basketball, as a simulation.
 *
 * Side-on and in feet, because the numbers then mean something you can argue
 * about: the thrower stands about four feet from the can at chest height, the
 * can is a thirteen-inch office bin, and gravity is the same twenty-four the
 * building falls at. A minigame in arbitrary units is a minigame nobody can
 * balance, and one that uses the world's own gravity is one where a throw that
 * felt right in the corridor feels right here.
 *
 * Pure and stepped. It owns no canvas, reads no input device and is driven
 * entirely by `aimAt` and `flick`, which is what lets the headless check play a
 * whole round in a loop and the panel be a hundred lines of drawing.
 *
 * The can slides on purpose. A still can is a ranging problem you solve once
 * and then repeat nine times; a sliding one has to be led, and leading a moving
 * target is the thing the throw in the actual game asks of you.
 */

/** Feet per second squared, and it is the world's figure rather than a nicer one. */
const GRAVITY = 24;

/**
 * Where the thrower's hand is. Chest height, and a foot and a half in from the
 * wall so there is a person there rather than a mark on the edge of the frame.
 */
export const ORIGIN = Object.freeze({ x: 1.6, y: 3.6 });

/** The can: a 13" bin, mouth and all, with its middle this far out. */
export const CAN = Object.freeze({ at: 12, mouth: 1.08, height: 2.3, depth: 1.08 });

/** The court, for whatever draws it. Feet. */
export const COURT = Object.freeze({ width: 17, height: 11 });

/**
 * How hard a flick is for a given reach of the cursor.
 *
 * The control is "point at where you want it to start going and how hard": the
 * launch velocity is the vector from the hand to the cursor, times this. Two
 * and a bit means the far corner of the court is a hard throw and the near air
 * in front of you is a lob, which is the range a player needs to find the can
 * at every speed it slides at.
 */
const FLICK = 2.15;

/** Nothing is thrown harder than this, however far off the court you point. */
const MAX_SPEED = 34;

/** A shot is over once it is on the floor or past the back wall. */
function offCourt(shot) {
  return shot.position.y <= 0 || shot.position.x > COURT.width || shot.position.x < -2;
}

export function createBasketball({ row, seed = 'basketball' }) {
  const rng = createRng(hashSeed(seed, row.level));
  /*
   * A phase off the seed, so the five levels are not the same round with a
   * faster can and two sittings of the same level are two different rounds.
   * Deterministic, though: the same seed always deals the same can, which is
   * what lets a test state what a round does rather than sample it.
   */
  const phase = rng.next() * Math.PI * 2;

  let elapsed = 0;
  let shotsTaken = 0;
  let makes = 0;
  let shot = null;
  let aim = { x: CAN.at, y: CAN.height };
  /** One line per shot: made, and where it ended. For the readout. */
  let log = [];

  function canX(t) {
    return CAN.at + Math.sin(t * row.canSpeed + phase) * (row.canRange / 2);
  }

  /** Did this step take the ball down through the mouth of the can? */
  function scored(from, to, x) {
    if (to.y > from.y) return false;
    const top = CAN.height;
    if (from.y < top || to.y > top) return false;
    const t = (from.y - top) / (from.y - to.y || 1);
    const at = from.x + (to.x - from.x) * t;
    return Math.abs(at - x) <= CAN.mouth / 2;
  }

  return {
    get level() {
      return row.level;
    },
    get target() {
      return row.target;
    },
    get score() {
      return makes;
    },
    get shotsLeft() {
      return row.shots - shotsTaken;
    },
    get inFlight() {
      return shot ? { ...shot.position } : null;
    },
    get aim() {
      return { ...aim };
    },
    /** Where the hand is and how high the mouth of the can sits. For the screen. */
    get hand() {
      return { ...ORIGIN };
    },
    get mouth() {
      return CAN.height;
    },
    get can() {
      return { x: canX(elapsed), y: 0, mouth: CAN.mouth, height: CAN.height, depth: CAN.depth };
    },

    /**
     * Where the can will be in `seconds`.
     *
     * Not a cheat and not a helper for the player: it is the information a
     * human already has, because they can see the can sliding and the whole
     * skill being asked for is reading that and leading it. A headless check
     * cannot see, so without this it would have to infer the slider by watching
     * it, and a check that reimplements the thing it is checking is not
     * checking anything.
     */
    canIn(seconds) {
      return canX(elapsed + seconds);
    },
    get done() {
      return shotsTaken >= row.shots && !shot;
    },
    get passed() {
      return makes >= row.target;
    },
    get log() {
      return [...log];
    },

    /** Where the cursor is, in court feet. Aiming is free and costs no shot. */
    aimAt(x, y) {
      aim = { x, y };
    },

    /**
     * Lets one go.
     *
     * Refused while one is in the air, which is the only rule the control has:
     * without it a player can empty the round in one frame and the sliding can
     * stops being a timing problem.
     */
    flick() {
      if (shot || shotsTaken >= row.shots) return false;
      const dx = aim.x - ORIGIN.x;
      const dy = aim.y - ORIGIN.y;
      const speed = Math.min(MAX_SPEED, Math.hypot(dx, dy) * FLICK);
      const length = Math.hypot(dx, dy) || 1;
      shot = {
        position: { ...ORIGIN },
        velocity: { x: (dx / length) * speed, y: (dy / length) * speed },
      };
      shotsTaken += 1;
      return true;
    },

    /**
     * The arc the current aim would throw, as a list of points.
     *
     * For the screen, and it is not a hint: it is the thing the control is. The
     * launch velocity is the vector from the hand to the cursor, which is not a
     * quantity anybody can read off a dashed straight line, so drawing where
     * that would actually put the ball is the difference between a game of skill
     * and a game of guessing what the mouse means. Where the *can* will be when
     * it gets there is still yours to work out.
     */
    ghost(steps = 42) {
      const dx = aim.x - ORIGIN.x;
      const dy = aim.y - ORIGIN.y;
      const length = Math.hypot(dx, dy) || 1;
      const speed = Math.min(MAX_SPEED, length * FLICK);
      let x = ORIGIN.x;
      let y = ORIGIN.y;
      let vx = (dx / length) * speed;
      let vy = (dy / length) * speed;
      const path = [{ x, y }];
      const step = 1 / 45;
      for (let i = 0; i < steps; i += 1) {
        vy -= GRAVITY * step;
        x += vx * step;
        y += vy * step;
        if (y <= 0 || x > COURT.width) {
          path.push({ x, y: Math.max(0, y) });
          break;
        }
        path.push({ x, y });
      }
      return path;
    },

    step(dt) {
      elapsed += dt;
      if (!shot) return null;
      const from = { ...shot.position };
      shot.velocity.y -= GRAVITY * dt;
      const to = {
        x: from.x + shot.velocity.x * dt,
        y: from.y + shot.velocity.y * dt,
      };
      shot.position = to;

      const x = canX(elapsed);
      if (scored(from, to, x)) {
        makes += 1;
        shot = null;
        log = [...log, { shot: shotsTaken, made: true, at: x }];
        return { made: true };
      }
      if (offCourt(shot)) {
        shot = null;
        log = [...log, { shot: shotsTaken, made: false, at: to.x }];
        return { made: false };
      }
      return null;
    },
  };
}
